<script>
  import { router, Link } from '@inertiajs/svelte'
  import Layout from '../../layouts/layout.svelte'
  import PageHeader from '../../components/page_header.svelte'
  import Card from '../../components/card.svelte'
  import StatGrid from '../../components/stat_grid.svelte'
  import StatTile from '../../components/stat_tile.svelte'
  import FilterBar from '../../components/filter_bar.svelte'
  import FilterDate from '../../components/filter_date.svelte'
  import LineChart from '../../components/charts/line_chart.svelte'
  import BarChart from '../../components/charts/bar_chart.svelte'
  import { Button, DataGrid } from '../../components/ui'

  let { metrics = null, filters = {}, presets = [] } = $props()

  let startDate = $state(filters.start_date || '')
  let endDate = $state(filters.end_date || '')

  // The server normalizes the range (swaps a reversed one, clamps to a year), so the
  // pickers follow whatever it applied.
  $effect(() => {
    startDate = filters.start_date || ''
    endDate = filters.end_date || ''
  })

  const applyFilters = (params = { start_date: startDate, end_date: endDate }) => {
    router.get('/engagement-dashboard', params, { preserveState: true, preserveScroll: true })
  }

  const summary = $derived(metrics?.summary ?? null)
  const activity = $derived(metrics?.activity_series ?? null)
  const points = $derived(metrics?.points ?? null)
  const commerce = $derived(metrics?.commerce ?? null)
  const membership = $derived(metrics?.membership ?? null)
  const retention = $derived(metrics?.retention ?? null)
  const referrals = $derived(metrics?.referrals ?? null)
  const leaderboards = $derived(metrics?.leaderboards ?? null)

  const num = (v) => (v ?? 0).toLocaleString()
  const dollars = (v) => `$${(v ?? 0).toLocaleString()}`
  const titleize = (s) => s.replaceAll('_', ' ').replace(/\b\w/g, (c) => c.toUpperCase())

  const leaderboardColumns = [
    { key: 'name', header: 'Name' },
    { key: 'count', header: 'Count', field: (row) => row.count.toLocaleString(), align: 'right' }
  ]
</script>

{#snippet leaderboard(title, rows)}
  <Card {title}>
    {#if rows?.length}
      <DataGrid columns={leaderboardColumns} {rows}>
        {#snippet cell(row, column)}
          {#if column.key === 'name'}
            <Link href={row.url} class="link link-hover">{row.name}</Link>
          {/if}
        {/snippet}
      </DataGrid>
    {:else}
      <p class="text-base-content/60">None in this range.</p>
    {/if}
  </Card>
{/snippet}

<Layout>
  <PageHeader title="Engagement Dashboard" subtitle="What users are doing across the app — activity, points, commerce, and retention" />

  <FilterBar>
    {#snippet start()}
      <FilterDate label="Start Date" value={startDate} onchange={(v) => (startDate = v)} />
      <FilterDate label="End Date" value={endDate} onchange={(v) => (endDate = v)} />
      <Button variant="primary" size="sm" onclick={() => applyFilters()}>Apply</Button>
    {/snippet}
    {#snippet end()}
      {#each presets as preset (preset.label)}
        <Button variant="ghost" size="sm" onclick={() => applyFilters({ start_date: preset.start_date, end_date: preset.end_date })}>
          {preset.label}
        </Button>
      {/each}
    {/snippet}
  </FilterBar>

  {#if !summary}
    <Card>
      <p class="text-base-content/60">No metrics available for this range.</p>
    </Card>
  {:else}
    <div class="space-y-6">
      <Card title="Summary">
        <p class="text-base-content/60 text-sm -mt-2 mb-2">Deltas compare against the previous period of the same length.</p>
        <StatGrid>
          <StatTile label="Active Users" value={num(summary.active_users.value)} delta={summary.active_users.delta} />
          <StatTile label="New Signups" value={num(summary.new_signups.value)} delta={summary.new_signups.delta} />
          <StatTile label="Check-ins" value={num(summary.checkins.value)} delta={summary.checkins.delta} />
          <StatTile label="Savings Delivered" value={dollars(summary.savings_dollars.value)} delta={summary.savings_dollars.delta} />
          <StatTile label="Points Earned" value={num(summary.points_earned.value)} delta={summary.points_earned.delta} />
        </StatGrid>
        <StatGrid>
          <StatTile label="Moments" value={num(summary.moments_created.value)} delta={summary.moments_created.delta} />
          <StatTile label="Hypes" value={num(summary.hypes_given.value)} delta={summary.hypes_given.delta} />
          <StatTile label="Quest Submissions" value={num(summary.quest_submissions.value)} delta={summary.quest_submissions.delta} />
          <StatTile label="Poll Votes" value={num(summary.poll_votes.value)} delta={summary.poll_votes.delta} />
        </StatGrid>
      </Card>

      <div class="grid gap-6 lg:grid-cols-2">
        <Card title="Daily Active Users">
          <LineChart
            labels={activity.labels}
            datasets={[
              { label: 'Active Users', data: activity.active_users },
              { label: 'New Signups', data: activity.new_signups }
            ]}
            height={300}
          />
        </Card>
        <Card title="Activity by Action">
          <LineChart
            labels={activity.labels}
            datasets={[
              { label: 'Check-ins', data: activity.checkins },
              { label: 'Moments', data: activity.moments_created },
              { label: 'Hypes', data: activity.hypes_given },
              { label: 'Quest Submissions', data: activity.quest_submissions },
              { label: 'Poll Votes', data: activity.poll_votes }
            ]}
            height={300}
          />
        </Card>
      </div>

      <div class="grid gap-6 lg:grid-cols-2">
        <Card title="Points Earned vs Spent">
          <LineChart
            labels={activity.labels}
            datasets={[
              { label: 'Earned', data: points.earned_by_day },
              { label: 'Spent', data: points.spent_by_day }
            ]}
            height={300}
          />
        </Card>
        <Card title="Action Mix (point ledger)">
          <BarChart
            labels={Object.keys(points.action_mix).map(titleize)}
            datasets={[{ label: 'Transactions', data: Object.values(points.action_mix) }]}
            horizontal
            height={300}
          />
        </Card>
      </div>

      <div class="grid gap-6 lg:grid-cols-2">
        <Card title="Savings vs Spend ($)">
          <LineChart
            labels={activity.labels}
            datasets={[
              { label: 'Savings', data: commerce.savings_by_day },
              { label: 'Spend', data: commerce.spend_by_day }
            ]}
            height={300}
          />
        </Card>
        <Card title="Store Purchases">
          <LineChart
            labels={activity.labels}
            datasets={[{ label: 'Purchases', data: commerce.store_purchases_by_day }]}
            height={300}
          />
        </Card>
      </div>

      <Card title="Membership">
        <StatGrid>
          <StatTile label="Current Members" value={num(membership.current_members)} />
          <StatTile label="Trialing" value={num(membership.status_counts.trialing)} />
          <StatTile label="Active" value={num(membership.status_counts.active)} />
          <StatTile label="Past Due" value={num(membership.status_counts.past_due)} highlight={(membership.status_counts.past_due ?? 0) > 0} />
          <StatTile label="Canceled" value={num(membership.status_counts.canceled)} />
        </StatGrid>
        <LineChart
          labels={activity.labels}
          datasets={[
            { label: 'Subscriptions Started', data: membership.starts_by_day },
            { label: 'Cancellations', data: membership.cancellations_by_day }
          ]}
          height={280}
        />
      </Card>

      <Card title="Retention Snapshot">
        <p class="text-base-content/60 text-sm -mt-2 mb-2">Point-in-time buckets by last activity — independent of the date range.</p>
        <StatGrid>
          <StatTile label="Active Last 24h" value={num(retention.active_1d)} />
          <StatTile label="Active Last 7d" value={num(retention.active_7d)} />
          <StatTile label="Active Last 30d" value={num(retention.active_30d)} />
          <StatTile label="Active Last 90d" value={num(retention.active_90d)} />
          <StatTile label="Dormant (90d+)" value={num(retention.dormant)} />
          <StatTile label="Never Active" value={num(retention.never_active)} />
        </StatGrid>
      </Card>

      <Card title="Referrals">
        <StatGrid>
          <StatTile label="Referred Signups" value={num(referrals.referred_signups)} />
        </StatGrid>
        {#if referrals.top_referrers?.length}
          <div class="grid gap-6 lg:grid-cols-2">
            <LineChart
              labels={activity.labels}
              datasets={[{ label: 'Referred Signups', data: referrals.referred_by_day }]}
              height={260}
            />
            <DataGrid columns={leaderboardColumns} rows={referrals.top_referrers}>
              {#snippet cell(row, column)}
                {#if column.key === 'name'}
                  <Link href={row.url} class="link link-hover">{row.name}</Link>
                {/if}
              {/snippet}
            </DataGrid>
          </div>
        {:else}
          <LineChart
            labels={activity.labels}
            datasets={[{ label: 'Referred Signups', data: referrals.referred_by_day }]}
            height={260}
          />
        {/if}
      </Card>

      <div class="grid gap-6 lg:grid-cols-3">
        {@render leaderboard('Most Active Users', leaderboards.top_users)}
        {@render leaderboard('Top Places by Check-ins', leaderboards.top_places)}
        {@render leaderboard('Top Adventures', leaderboards.top_adventures)}
      </div>
    </div>
  {/if}
</Layout>
